import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { mensagensAPI } from '../utils/api';
import { 
  Users, 
  Package, 
  FileText, 
  UserCog, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  LayoutDashboard,
  Mail
} from 'lucide-react';
import { Button } from './ui/button';

const LOGO_URL = "https://drlp.atlantic4web.pt/logo-drleonardopacheco.jpg";
const APP_NAME = "Dr. Leonardo Pacheco";

const menuItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/mensagens', label: 'Mensagens', icon: Mail },
  { path: '/clientes', label: 'Clientes', icon: Users },
  { path: '/produtos', label: 'Produtos', icon: Package },
  { path: '/receitas', label: 'Receitas', icon: FileText },
  { path: '/utilizadores', label: 'Utilizadores', icon: UserCog },
  { path: '/definicoes', label: 'Definições', icon: Settings },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [newMsgCount, setNewMsgCount] = useState(0);

  const fetchMsgCount = useCallback(async () => {
    try {
      const res = await mensagensAPI.countNovas();
      const count = res.data.count ?? res.data;
      if (count > newMsgCount && newMsgCount >= 0) {
        // Show notification for new messages
        if (count > 0 && Notification.permission === 'granted') {
          new Notification('Nova mensagem da receção', { body: `Tem ${count} mensagem(ns) nova(s)` });
        }
      }
      setNewMsgCount(count);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchMsgCount();
    const interval = setInterval(fetchMsgCount, 10000);
    // Request notification permission
    if (Notification.permission === 'default') Notification.requestPermission();
    return () => clearInterval(interval);
  }, [fetchMsgCount]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="main-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50 flex items-center px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        <div className="flex items-center gap-2 ml-3">
          <img src={LOGO_URL} alt="Logo" className="h-8 w-8 rounded-full object-cover" />
          <span className="font-semibold text-slate-900 text-sm">{APP_NAME}</span>
        </div>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-50 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-20 flex items-center gap-3 px-4 border-b border-slate-200">
          <img src={LOGO_URL} alt="Logo" className="h-12 w-12 rounded-full object-cover" />
          <span className="text-sm font-bold text-slate-900 tracking-tight leading-tight">{APP_NAME}</span>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive 
                    ? 'bg-amber-50 text-amber-800 border border-amber-300' 
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
                data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
              >
                <Icon className={`h-5 w-5 ${isActive ? 'text-amber-600' : 'text-slate-400'}`} />
                {item.label}
                {item.path === '/mensagens' && newMsgCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center" data-testid="msg-badge">
                    {newMsgCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center">
              <span className="text-sm font-semibold text-amber-700">
                {user?.nome?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.nome}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full mt-2 justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Terminar Sessão
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen flex flex-col">
        <div className="p-6 lg:p-8 flex-1">
          {children}
        </div>
        {/* Footer */}
        <footer className="p-4 text-center border-t border-slate-200 bg-white">
          <p className="text-xs text-slate-500">
            2026 © Dr. Leonardo Pacheco
          </p>
          <p className="text-xs text-slate-400">
            Desenvolvido por <a href="https://atlantic4web.pt" target="_blank" rel="noopener noreferrer" className="font-medium hover:text-amber-600">atlantic4web</a>
          </p>
        </footer>
      </main>
    </div>
  );
};
